/**
 * localStorage 工具函數 | localStorage Utility Functions
 * 提供人員資料與歷史記錄的本地存儲 | Local storage for person data and history
 */

import { Person } from '@/types/person';
import { HistoryEntry } from '@/types/history';

/**
 * localStorage 鍵值常量 | localStorage Key Constants
 */
export const STORAGE_KEYS = {
  PERSONS: 'ds-persons',           // 人員資料 | Person data
  HISTORY: 'ds-history',           // 歷史記錄 | History records
  VERSION: 'ds-version',           // 資料版本 | Data version
} as const;

/**
 * 目前資料版本 | Current Data Version
 */
const CURRENT_VERSION = '1.0.0';

/**
 * 檢查 localStorage 是否可用 | Check if localStorage is available
 */
export const isLocalStorageAvailable = (): boolean => {
  try {
    const test = '__localStorage_test__';
    localStorage.setItem(test, test);
    localStorage.removeItem(test);
    return true;
  } catch (e) {
    console.warn('localStorage 不可用 | localStorage is not available:', e);
    return false;
  }
};

/**
 * 保存人員資料到 localStorage | Save person data to localStorage
 */
export const savePersonsToStorage = (persons: Person[]): boolean => {
  if (!isLocalStorageAvailable()) return false;

  try {
    localStorage.setItem(STORAGE_KEYS.PERSONS, JSON.stringify(persons));
    localStorage.setItem(STORAGE_KEYS.VERSION, CURRENT_VERSION);
    return true;
  } catch (e) {
    console.error('保存人員資料失敗 | Failed to save person data:', e);
    return false;
  }
};

/**
 * 從 localStorage 載入人員資料 | Load person data from localStorage
 */
export const loadPersonsFromStorage = (): Person[] | null => {
  if (!isLocalStorageAvailable()) return null;

  try {
    const data = localStorage.getItem(STORAGE_KEYS.PERSONS);
    if (!data) return null;

    const persons = JSON.parse(data) as Person[];
    return persons;
  } catch (e) {
    console.error('載入人員資料失敗 | Failed to load person data:', e);
    return null;
  }
};

/**
 * 保存歷史記錄到 localStorage | Save history to localStorage
 */
export const saveHistoryToStorage = (history: HistoryEntry[]): boolean => {
  if (!isLocalStorageAvailable()) return false;

  try {
    localStorage.setItem(STORAGE_KEYS.HISTORY, JSON.stringify(history));
    return true;
  } catch (e) {
    console.error('保存歷史記錄失敗 | Failed to save history:', e);
    return false;
  }
};

/**
 * 從 localStorage 載入歷史記錄 | Load history from localStorage
 */
export const loadHistoryFromStorage = (): HistoryEntry[] | null => {
  if (!isLocalStorageAvailable()) return null;

  try {
    const data = localStorage.getItem(STORAGE_KEYS.HISTORY);
    if (!data) return null;

    const history = JSON.parse(data) as HistoryEntry[];
    return history;
  } catch (e) {
    console.error('載入歷史記錄失敗 | Failed to load history:', e);
    return null;
  }
};

/**
 * 清除所有應用程式資料 | Clear all application data
 * 這會清除本網站的所有 localStorage 資料 | Clears all localStorage data for this website
 */
export const clearAllStorage = (): boolean => {
  if (!isLocalStorageAvailable()) return false;

  try {
    // 獲取所有 localStorage 鍵值 | Get all localStorage keys
    const keys = Object.keys(localStorage);

    // 只清除本應用程式的資料（以 'ds-' 開頭）| Clear only this app's data (starts with 'ds-')
    const appKeys = keys.filter(key => key.startsWith('ds-'));
    appKeys.forEach(key => localStorage.removeItem(key));

    console.log(`已清除 ${appKeys.length} 個應用程式資料項 | Cleared ${appKeys.length} app data items`);
    return true;
  } catch (e) {
    console.error('清除資料失敗 | Failed to clear data:', e);
    return false;
  }
};

/**
 * 清除歷史記錄 | Clear history only
 */
export const clearHistoryStorage = (): boolean => {
  if (!isLocalStorageAvailable()) return false;

  try {
    localStorage.removeItem(STORAGE_KEYS.HISTORY);
    return true;
  } catch (e) {
    console.error('清除歷史記錄失敗 | Failed to clear history:', e);
    return false;
  }
};

/**
 * 獲取存儲空間使用情況 | Get storage usage information
 */
export const getStorageInfo = (): { used: number; total: number; percentage: number } => {
  if (!isLocalStorageAvailable()) {
    return { used: 0, total: 0, percentage: 0 };
  }

  try {
    let total = 0;
    const keys = Object.keys(localStorage);

    keys.forEach(key => {
      const value = localStorage.getItem(key);
      if (value) {
        total += key.length + value.length;
      }
    });

    // 估算 localStorage 限制（通常 5-10MB）| Estimate localStorage limit (usually 5-10MB)
    const estimatedLimit = 5 * 1024 * 1024; // 5MB
    const percentage = (total / estimatedLimit) * 100;

    return {
      used: total,
      total: estimatedLimit,
      percentage: Math.round(percentage * 100) / 100,
    };
  } catch (e) {
    console.error('獲取存儲資訊失敗 | Failed to get storage info:', e);
    return { used: 0, total: 0, percentage: 0 };
  }
};

/**
 * 獲取資料版本 | Get data version
 */
export const getDataVersion = (): string => {
  if (!isLocalStorageAvailable()) return 'unknown';

  try {
    return localStorage.getItem(STORAGE_KEYS.VERSION) || 'unknown';
  } catch (e) {
    return 'unknown';
  }
};
